export const MS_PER_DAY = 24 * 60 * 60 * 1000

// Format a Date as a local "yyyy-mm-dd" calendar string. Historically this
// helper used `.toISOString()` (UTC), which silently shifts the date one
// day forward in any timezone west of UTC during evening hours — every
// date-range default that landed via this helper rendered tomorrow's
// calendar day. "yyyy-mm-dd" should always be a local calendar intent;
// use this helper as the single source of truth for that conversion.
export const dateWithoutTime = (date) => {
  if (!date) return null
  const yyyy = date.getFullYear()
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const dd = String(date.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

// Shifts an ISO date one year back. Caps Feb 29 to Feb 28 in non-leap years (matches
// ActiveSupport's `Date#prev_year` and avoids `setFullYear` timezone-dependent overflow).
export function shiftYearBack(isoDate) {
  const [year, month, day] = isoDate.split('-').map(Number)
  const lastDayOfTargetMonth = new Date(year - 1, month, 0).getDate()
  const pad = (n) => String(n).padStart(2, '0')
  return `${year - 1}-${pad(month)}-${pad(Math.min(day, lastDayOfTargetMonth))}`
}

export const daysAgo = (n, from, returnUnformatted) => {
  const d = from ? new Date(from) : new Date()
  d.setDate(d.getDate() - n)

  if (returnUnformatted) {
    return d
  }

  return dateWithoutTime(d)
}

const DEFAULT_DATETIME_OPTIONS = {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit'
}

const DEFAULT_DATE_OPTIONS = {
  month: 'short',
  day: 'numeric',
  year: 'numeric'
}

// Time-of-day preset ('16:30') for formatDateInZone.
export const TIME_ONLY_OPTIONS = {
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23'
}

export function formatDate(dateStr) {
  if (!dateStr) return '—'
  return new Date(dateStr).toLocaleDateString('en-US', DEFAULT_DATE_OPTIONS)
}

// Formatting resolved in an explicit IANA zone, so a business's local
// reading doesn't drift with the viewer's. Defaults to the project date
// format ('Aug 7, 2026'); pass other Intl options to change what is shown,
// e.g. TIME_ONLY_OPTIONS for the 'HH:mm' wall clock. Returns '' for an
// unknown zone so callers can hide the value instead of crashing.
export function formatDateInZone(iso, timeZone = 'UTC', options = DEFAULT_DATE_OPTIONS) {
  if (!iso) return ''
  try {
    return new Date(iso).toLocaleString('en-US', { ...options, timeZone })
  } catch {
    return ''
  }
}

// Month + day only, e.g. 'Jul 1'. Pass an explicit IANA zone when the value
// is a calendar date rather than a viewer-local moment.
export function formatMonthDay(value, timeZone = undefined) {
  if (!value) return ''
  return new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone })
}

// Human-readable timezone name for display, e.g. 'America/Los_Angeles' ->
// 'Pacific Time'. Falls back to the raw IANA id if the runtime can't resolve
// a generic name (older browsers / obscure zones).
export function formatTimeZone(timeZone) {
  if (!timeZone) return ''
  try {
    const name = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'longGeneric' })
      .formatToParts(new Date())
      .find((part) => part.type === 'timeZoneName')?.value
    return name || timeZone
  } catch {
    return timeZone
  }
}

export function formatDateTime(dateStr, options = {}) {
  if (!dateStr) return '-'
  const date = new Date(dateStr)
  return date.toLocaleString('en-US', { ...DEFAULT_DATETIME_OPTIONS, ...options })
}

// Current wall-clock time in an IANA zone, as a local Date whose fields read
// as that zone's clock. Use it for datepicker floors on values that are
// wall-clock times in an explicit zone (e.g. a business's schedule): there
// `new Date()` is the viewer's clock, which wrongly blocks times that are
// still in the future at the location. Falls back to browser-local now when
// the zone is missing or unknown.
export function nowInTimeZone(timeZone) {
  const now = new Date()
  if (!timeZone) return now
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23'
    }).formatToParts(now)
    const get = (type) => Number(parts.find((part) => part.type === type)?.value)
    return new Date(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'))
  } catch {
    return now
  }
}

// Accepts Date, number (timestamp in ms), or a date/time string.
export function isInThePast(value) {
  if (value == null) return false
  const timestamp = new Date(value).getTime()
  if (Number.isNaN(timestamp)) return false
  return timestamp < Date.now()
}

const ISO_DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/

// Parse a date-only value into a local Date. Date pickers (and quick-range
// presets) commonly hand back "yyyy-mm-dd" strings, which JavaScript parses
// as UTC midnight via `new Date('yyyy-mm-dd')`. In US time zones that
// shifts the date back a day, which then leaks into form payloads sent to
// Rails. Use this helper any time you need a Date constructed from a
// date-only string.
export function parseLocalDateOnly(value) {
  if (!value) return null
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value
  }
  if (typeof value === 'string') {
    const m = value.match(ISO_DATE_ONLY)
    if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  }
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

// Inverse of parseLocalDateOnly — emit "yyyy-mm-dd" using the local
// calendar date of the input. Safe to feed AppDatepicker output (Date
// objects on click-throughs, strings on quick-range presets).
export function formatLocalDateOnly(value) {
  const date = parseLocalDateOnly(value)
  if (!date) return null
  const yyyy = date.getFullYear()
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const dd = String(date.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}
